import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../Context/AuthContext';
import { FaCheck, FaClock, FaMoneyBill, FaLightbulb, FaExclamationTriangle, FaTimes, FaTrophy, FaGamepad, FaStar, FaFire, FaChartLine, FaCalendarAlt, FaListUl, FaDownload, FaSearch, FaChevronUp, FaChevronDown, FaCreditCard, FaPercentage, FaDollarSign, FaBolt, FaEye, FaSpinner, FaShoppingCart, FaSync, FaArrowRight, FaHourglassHalf, FaBell } from 'react-icons/fa';
import './MyTrades.css';

import { apiFetch } from '../../services/apiClient';

const STATUS_CONFIG = {
  COMPLETED:       { label: 'Completado',     color: '#00b248', bg: 'rgba(0,178,72,0.1)',    Icon: FaCheck },
  WAITING_PAYMENT: { label: 'Esperando pago', color: '#0091ea', bg: 'rgba(0,145,234,0.1)',   Icon: FaClock },
  PAID:            { label: 'Pagado',          color: '#f0b90b', bg: 'rgba(240,185,11,0.1)',  Icon: FaMoneyBill },
  CREATED:         { label: 'En curso',        color: '#0091ea', bg: 'rgba(0,145,234,0.1)',   Icon: FaLightbulb },
  DISPUTED:        { label: 'Disputa',         color: '#f4511e', bg: 'rgba(244,81,30,0.1)',   Icon: FaExclamationTriangle },
  CANCELLED:       { label: 'Cancelado',       color: '#888',    bg: 'rgba(136,136,136,0.1)', Icon: FaTimes },
};

const FILTER_OPTIONS = [
  { key: 'all',             Icon: FaGamepad, label: 'Todos'        },
  { key: 'COMPLETED',       Icon: FaCheck, label: 'Completados'    },
  { key: 'WAITING_PAYMENT', Icon: FaClock, label: 'En curso'       },
  { key: 'DISPUTED',        Icon: FaExclamationTriangle, label: 'Disputas'      },
  { key: 'CANCELLED',       Icon: FaTimes, label: 'Cancelados'     },
];

const PAGE_SIZE = 5;

function getLevelColor(level) {
  if (level >= 10) return '#f4511e';
  if (level >= 7)  return '#8e24aa';
  if (level >= 4)  return '#0091ea';
  return '#888';
}

function exportCSV(trades) {
  const headers = ['ID','Tipo','Contraparte','Monto','Moneda','Red','Pago','Estado','Fecha'];
  const rows    = trades.map(t => [
    t.id, 'Trade',
    t.buyer?.username ?? t.seller?.username ?? '—',
    t.amount, t.crypto, t.blockchain, t.payment,
    STATUS_CONFIG[t.status]?.label ?? t.status,
    new Date(t.createdAt).toLocaleDateString(),
  ]);
  const csv  = [headers, ...rows].map(r => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url; link.download = `bitplify_trades_${new Date().toISOString().slice(0,10)}.csv`;
  link.click(); URL.revokeObjectURL(url);
}

// ===========================
// CONTADOR EN VIVO (hasta el deadline de pago)
// ===========================
function Countdown({ deadline }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!deadline) return null;
  const ms = new Date(deadline).getTime() - now;
  if (ms <= 0) return <span className="mt-cd expired"><FaHourglassHalf style={{ marginRight: 4 }} /> Expirado</span>;
  const sec = Math.floor(ms / 1000);
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  const txt = h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  const urgent = ms < 5 * 60 * 1000;
  return <span className={`mt-cd ${urgent ? 'urgent' : ''}`}><FaClock style={{ marginRight: 4 }} /> {txt}</span>;
}

// Qué tiene que pasar ahora, según estado y rol
function nextAction(status, isBuyer) {
  if (status === 'WAITING_PAYMENT') return isBuyer
    ? { text: 'Pagá y subí el comprobante', tone: 'do',   mine: true }
    : { text: 'Esperando el pago del comprador', tone: 'wait', mine: false };
  if (status === 'PAID') return isBuyer
    ? { text: 'Esperando que el vendedor libere la cripto', tone: 'wait', mine: false }
    : { text: 'Confirmá el pago recibido y liberá la cripto', tone: 'do', mine: true };
  if (status === 'DISPUTED') return { text: 'En disputa — nuestro equipo la está revisando', tone: 'dispute', mine: false };
  return { text: 'En curso', tone: 'wait', mine: false };
}

// ===========================
// PANEL DE TRADES ACTIVOS — lo más importante: qué requiere tu acción AHORA
// ===========================
function ActiveTradeCard({ t, currentUserId }) {
  const navigate = useNavigate();
  const isBuyer = t.buyerId === currentUserId;
  const cp      = isBuyer ? t.seller : t.buyer;
  const act     = nextAction(t.status, isBuyer);
  const initials = (cp?.name ?? cp?.username ?? 'U').slice(0, 2).toUpperCase();

  return (
    <div className={`mt-active-card ${act.mine ? 'mine' : ''} tone-${act.tone}`} onClick={() => navigate(`/trades/${t.id}`)}>
      <div className="mt-active-top">
        <div className="mt-active-cp">
          <div className="mt-active-avatar">
            {cp?.avatar ? <img src={cp.avatar} alt="" /> : initials}
          </div>
          <div className="mt-active-who">
            <span className={`mt-role ${isBuyer ? 'buy' : 'sell'}`}>{isBuyer ? <><FaShoppingCart style={{ marginRight: 4 }} /> Compra</> : <><FaMoneyBill style={{ marginRight: 4 }} /> Venta</>}</span>
            <strong>@{cp?.username ?? '—'}</strong>
          </div>
        </div>
        <div className="mt-active-amt">
          <span className="fin-num">USDT {Number(t.amount).toLocaleString()}</span>
          <small>{t.payment}</small>
        </div>
      </div>
      <div className="mt-active-bottom">
        <span className={`mt-active-action ${act.mine ? 'mine' : ''}`}>
          {act.mine ? <FaBell style={{ marginRight: 6 }} /> : <FaHourglassHalf style={{ marginRight: 6 }} />} {act.text}
        </span>
        <div className="mt-active-right">
          {t.status === 'WAITING_PAYMENT' && <Countdown deadline={t.paymentDeadline} />}
          <button className="mt-active-go" onClick={e => { e.stopPropagation(); navigate(`/trades/${t.id}`); }}>
            Ir al trade <FaArrowRight style={{ marginLeft: 4 }} />
          </button>
        </div>
      </div>
    </div>
  );
}

function ActiveTradesPanel({ trades, currentUserId, onRefresh, refreshing }) {
  const active = useMemo(() => {
    const ACTIVE = ['WAITING_PAYMENT', 'PAID', 'DISPUTED'];
    return trades
      .filter(t => ACTIVE.includes(t.status))
      .sort((a, b) => {
        // primero los que requieren MI acción, después por deadline más cercano
        const am = nextAction(a.status, a.buyerId === currentUserId).mine ? 0 : 1;
        const bm = nextAction(b.status, b.buyerId === currentUserId).mine ? 0 : 1;
        if (am !== bm) return am - bm;
        return new Date(a.paymentDeadline ?? a.createdAt) - new Date(b.paymentDeadline ?? b.createdAt);
      });
  }, [trades, currentUserId]);

  if (active.length === 0) return null;
  const mineCount = active.filter(t => nextAction(t.status, t.buyerId === currentUserId).mine).length;

  return (
    <section className="mt-section mt-active-section">
      <div className="mt-section-header">
        <h3 className="mt-section-title"><FaBolt style={{ marginRight: 8 }} /> Trades activos</h3>
        <div className="mt-section-actions">
          {mineCount > 0 && <span className="mt-action-badge"><FaBell style={{ marginRight: 5 }} /> {mineCount} requiere{mineCount > 1 ? 'n' : ''} tu acción</span>}
          <button className="mt-export-btn" onClick={onRefresh} disabled={refreshing} title="Actualizar">
            <FaSync className={refreshing ? 'mt-spin' : ''} style={{ marginRight: 6 }} /> Actualizar
          </button>
        </div>
      </div>
      <div className="mt-active-grid">
        {active.map(t => <ActiveTradeCard key={t.id} t={t} currentUserId={currentUserId} />)}
      </div>
    </section>
  );
}

// ===========================
// STATS HERO
// ===========================
function StatsHero({ user }) {
  if (!user) return null;
  const xpMax      = (user.level ?? 1) * 2000;
  const xpPct      = Math.min(((user.xp ?? 0) / xpMax) * 100, 100);
  const levelColor = getLevelColor(user.level ?? 1);
  return (
    <section className="mt-hero">
      <div className="mt-hero-left">
        <div className="mt-hero-avatar" style={{ borderColor: levelColor }}>
          {user.avatar
            ? <img src={user.avatar} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
            : (user.name ?? 'U').slice(0, 2).toUpperCase()
          }
        </div>
        <div className="mt-hero-info">
          <div className="mt-hero-name-row">
            <h2 className="mt-hero-name">{user.name}</h2>
            <span className="fin-chip mt-hero-badge"><FaTrophy style={{marginRight: '4px'}} /> {user.rank ?? 'Novato'}</span>
          </div>
          <div className="mt-hero-level" style={{ color: levelColor }}>
            <FaGamepad style={{marginRight: '4px'}} /> Nivel <span className="fin-num">{user.level ?? 1}</span> · <FaFire style={{marginRight: '4px'}} /> <span className="fin-num">{user.streak ?? 0}</span> días en racha
          </div>
          <div className="mt-xp-wrap">
            <div className="mt-xp-bar"><div className="mt-xp-fill" style={{ width: `${xpPct}%`, background: levelColor }} /></div>
            <span className="mt-xp-label fin-num">{(user.xp ?? 0).toLocaleString()} / {xpMax.toLocaleString()} XP ({Math.round(xpPct)}%)</span>
          </div>
        </div>
      </div>
    </section>
  );
}

// ===========================
// KPI CARDS
// ===========================
function KPICards({ trades, currentUserId }) {
  const completed = trades.filter(t => t.status === 'COMPLETED');
  const volume    = completed.reduce((s, t) => s + Number(t.amount ?? 0), 0);
  const active    = trades.filter(t => t.status === 'WAITING_PAYMENT' || t.status === 'PAID').length;
  const bought    = completed.filter(t => t.buyerId === currentUserId).reduce((s, t) => s + Number(t.amount ?? 0), 0);
  const sold      = completed.filter(t => t.sellerId === currentUserId).reduce((s, t) => s + Number(t.amount ?? 0), 0);
  const fmt = (n) => n >= 1000 ? `$${(n / 1000).toFixed(1)}K` : `$${n.toLocaleString()}`;
  const kpis = [
    { Icon: FaMoneyBill, label: 'Volumen Total',  value: fmt(volume),      sub: `Compraste ${fmt(bought)} · Vendiste ${fmt(sold)}`, color: '#00b248' },
    { Icon: FaCheck,     label: 'Completados',     value: completed.length, sub: 'De todos los tiempos', color: '#0091ea' },
    { Icon: FaLightbulb, label: 'Activos',         value: active,           sub: 'En este momento',      color: '#f0b90b' },
    { Icon: FaStar,      label: 'Total Trades',    value: trades.length,    sub: 'Historial completo',   color: '#8e24aa' },
  ];
  return (
    <section className="mt-kpis">
      {kpis.map((k, i) => (
        <div className="mt-kpi-card" key={i} style={{ '--kpi-color': k.color }}>
          <div className="mt-kpi-icon"><k.Icon size={24} /></div>
          <div className="mt-kpi-body">
            <span className="fin-label mt-kpi-label">{k.label}</span>
            <span className="mt-kpi-value fin-num" style={{ color: k.color }}>{k.value}</span>
            <span className="mt-kpi-sub">{k.sub}</span>
          </div>
        </div>
      ))}
    </section>
  );
}

// ===========================
// GRÁFICO DE VOLUMEN ✅ nuevo
// ===========================
function VolumeChart({ trades }) {
  // Agrupa trades completados por mes (últimos 6 meses)
  const months = useMemo(() => {
    const now    = new Date();
    const result = [];
    for (let i = 5; i >= 0; i--) {
      const d     = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const label = d.toLocaleString('es', { month: 'short' });
      const key   = `${d.getFullYear()}-${d.getMonth()}`;
      const vol   = trades
        .filter(t => t.status === 'COMPLETED')
        .filter(t => {
          const td = new Date(t.createdAt);
          return td.getFullYear() === d.getFullYear() && td.getMonth() === d.getMonth();
        })
        .reduce((s, t) => s + Number(t.amount ?? 0), 0);
      result.push({ label, key, vol });
    }
    return result;
  }, [trades]);

  const maxVol = Math.max(...months.map(m => m.vol), 1);

  return (
    <section className="mt-section">
      <div className="mt-section-header">
        <h3 className="mt-section-title"><FaChartLine style={{marginRight: '8px'}} /> Volumen mensual</h3>
        <span className="mt-section-sub">Últimos 6 meses · solo completados</span>
      </div>
      <div className="mt-chart">
        {months.map((m, i) => (
          <div key={m.key} className="mt-chart-col">
            <span className="mt-chart-val fin-num">
              {m.vol > 0 ? `$${m.vol >= 1000 ? (m.vol/1000).toFixed(1)+'K' : m.vol}` : ''}
            </span>
            <div className="mt-chart-bar-wrap">
              <div
                className="mt-chart-bar"
                style={{ height: `${Math.max((m.vol / maxVol) * 100, m.vol > 0 ? 4 : 0)}%` }}
              />
            </div>
            <span className="mt-chart-label fin-label">{m.label}</span>
          </div>
        ))}
      </div>
      {maxVol === 1 && (
        <p style={{ textAlign: 'center', fontSize: '0.8rem', color: 'var(--mt-text-tertiary)', marginTop: 8 }}>
          Completá trades para ver tu volumen aquí.
        </p>
      )}
    </section>
  );
}

// ===========================
// MAPA DE ACTIVIDAD ✅ conectado a datos reales
// ===========================
function ActivityMap({ trades }) {
  const days = ['D','L','M','X','J','V','S'];

  // Construir mapa de días con actividad real
  const activityMap = useMemo(() => {
    const map = {};
    trades.forEach(t => {
      const d   = new Date(t.createdAt);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      map[key]  = (map[key] ?? 0) + 1;
    });
    return map;
  }, [trades]);

  // Últimas 7 semanas (49 días)
  const cells = useMemo(() => {
    const result = [];
    const now    = new Date();
    for (let i = 48; i >= 0; i--) {
      const d   = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      result.push({ key, count: activityMap[key] ?? 0, date: d.toLocaleDateString() });
    }
    return result;
  }, [activityMap]);

  const totalDays  = cells.filter(c => c.count > 0).length;
  const totalTrades = cells.reduce((s, c) => s + c.count, 0);

  return (
    <section className="mt-section">
      <div className="mt-section-header">
        <h3 className="mt-section-title"><FaCalendarAlt style={{marginRight: '8px'}} /> Actividad de trading</h3>
        <span className="mt-section-sub"><span className="fin-num">{totalDays}</span> días activos · <span className="fin-num">{totalTrades}</span> trades</span>
      </div>
      <div className="mt-activity-wrap">
        <div className="mt-activity-days">{days.map(d => <span key={d}>{d}</span>)}</div>
        <div className="mt-activity-grid">
          {cells.map((a, i) => (
            <div
              key={i}
              className="mt-activity-cell"
              style={{ opacity: a.count === 0 ? 0.12 : Math.min(0.3 + a.count * 0.2, 1) }}
              title={`${a.date}: ${a.count} trade${a.count !== 1 ? 's' : ''}`}
            />
          ))}
        </div>
      </div>
      <div className="mt-activity-legend">
        <span>Menos</span>
        {[0.12, 0.3, 0.5, 0.7, 0.9].map((o, i) => (
          <div key={i} className="mt-activity-cell" style={{ opacity: o, width: 14, height: 14, borderRadius: 3 }} />
        ))}
        <span>Más</span>
      </div>
    </section>
  );
}

// ===========================
// HISTORIAL DE TRADES ✅ con filtro de fechas y navigate
// ===========================
function TradeHistory({ trades, currentUserId }) {
  const navigate = useNavigate();

  const [filter, setFilter]       = useState('all');
  const [search, setSearch]       = useState('');
  const [expanded, setExpanded]   = useState(null);
  const [page, setPage]           = useState(1);
  const [dateFrom, setDateFrom]   = useState('');
  const [dateTo, setDateTo]       = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);

  const filtered = useMemo(() => {
    return trades.filter(t => {
      const matchStatus    = filter === 'all' || t.status === filter;
      const counterpart    = t.buyerId === currentUserId ? t.seller?.username : t.buyer?.username;
      const matchSearch    = (counterpart ?? '').toLowerCase().includes(search.toLowerCase()) || String(t.id).includes(search);
      const tradeDate      = new Date(t.createdAt);
      const matchDateFrom  = dateFrom ? tradeDate >= new Date(dateFrom) : true;
      const matchDateTo    = dateTo   ? tradeDate <= new Date(dateTo + 'T23:59:59') : true;
      return matchStatus && matchSearch && matchDateFrom && matchDateTo;
    });
  }, [trades, filter, search, dateFrom, dateTo, currentUserId]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleFilter = (key) => { setFilter(key); setPage(1); setExpanded(null); };
  const handleSearch = (e)   => { setSearch(e.target.value); setPage(1); setExpanded(null); };
  const clearDates   = ()    => { setDateFrom(''); setDateTo(''); setPage(1); };

  const hasDateFilter = dateFrom || dateTo;

  return (
    <section className="mt-section">
      <div className="mt-section-header">
        <h3 className="mt-section-title"><FaListUl style={{marginRight: '8px'}} /> Historial de Trades</h3>
        <div className="mt-section-actions">
          <span className="mt-section-sub"><span className="fin-num">{filtered.length}</span> operaciones</span>
          <button
            className={`mt-export-btn ${showDateFilter ? 'active' : ''}`}
            onClick={() => setShowDateFilter(p => !p)}
            title="Filtrar por fechas"
          >
            <FaCalendarAlt style={{marginRight: '6px'}} /> Fechas {hasDateFilter && '●'}
          </button>
          <button className="mt-export-btn" onClick={() => exportCSV(filtered)} title="Exportar a CSV">
            <FaDownload style={{marginRight: '6px'}} /> Exportar CSV
          </button>
        </div>
      </div>

      {/* ✅ Filtro de fechas */}
      {showDateFilter && (
        <div className="mt-date-filter">
          <div className="mt-date-field">
            <label>Desde</label>
            <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} className="mt-date-input" />
          </div>
          <div className="mt-date-field">
            <label>Hasta</label>
            <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} className="mt-date-input" />
          </div>
          {hasDateFilter && (
            <button className="mt-date-clear" onClick={clearDates}><FaTimes style={{marginRight: '4px'}} /> Limpiar</button>
          )}
        </div>
      )}

      <div className="mt-filters">
        {FILTER_OPTIONS.map(f => (
          <button key={f.key} className={`mt-filter-btn ${filter === f.key ? 'active' : ''}`} onClick={() => handleFilter(f.key)}><f.Icon style={{marginRight: '4px'}} /> {f.label}</button>
        ))}
        <div className="mt-search-wrap">
          <FaSearch className="mt-search-icon" />
          <input className="mt-search" placeholder="Buscar trader o ID..." value={search} onChange={handleSearch} />
        </div>
      </div>

      {paginated.length === 0 ? (
        <div className="mt-empty"><span><FaSearch /></span><p>No se encontraron trades con ese filtro.</p></div>
      ) : (
        <>
          <div className="mt-table-wrap">
            <table className="mt-table">
              <thead>
                <tr>
                  <th>ID</th><th>Contraparte</th><th>Monto</th><th>Cripto</th>
                  <th>Red</th><th>Estado</th><th>Fecha</th><th></th>
                </tr>
              </thead>
              <tbody>
                {paginated.map(t => {
                  const st          = STATUS_CONFIG[t.status] ?? STATUS_CONFIG['CREATED'];
                  const isBuyer     = t.buyerId === currentUserId;
                  const counterpart = isBuyer ? t.seller?.username : t.buyer?.username;
                  const isActive    = t.status === 'WAITING_PAYMENT' || t.status === 'PAID';
                  return (
                    <React.Fragment key={t.id}>
                      <tr className={`mt-row ${expanded === t.id ? 'expanded' : ''}`} onClick={() => setExpanded(expanded === t.id ? null : t.id)}>
                        <td className="mt-id fin-num">T-{t.id}</td>
                        <td className="mt-counterpart">
                          <span className={`mt-role ${isBuyer ? 'buy' : 'sell'}`}>{isBuyer ? 'Compra' : 'Venta'}</span>
                          @{counterpart ?? '—'}
                        </td>
                        <td className="mt-amount fin-num">USDT {Number(t.amount).toLocaleString()}</td>
                        <td><span className="fin-chip">{t.crypto}</span></td>
                        <td><span className="fin-chip">{t.blockchain}</span></td>
                        <td><span className="fin-chip mt-status-badge" style={{ color: st.color, borderColor: st.color + '55' }}><st.Icon size={11} style={{marginRight: '4px'}} /> {st.label}</span></td>
                        <td className="mt-date fin-num">{new Date(t.createdAt).toLocaleDateString()}</td>
                        <td className="mt-expand-btn">{expanded === t.id ? <FaChevronUp /> : <FaChevronDown />}</td>
                      </tr>
                      {expanded === t.id && (
                        <tr className="mt-detail-row">
                          <td colSpan="8">
                            <div className="mt-detail">
                              <div className="mt-detail-item"><span className="fin-label"><FaCreditCard style={{marginRight: '4px'}} /> Método</span><strong>{t.payment}</strong></div>
                              <div className="mt-detail-item"><span className="fin-label"><FaPercentage style={{marginRight: '4px'}} /> Spread</span><strong className="fin-num">{t.spread > 0 ? `+${t.spread}%` : `${t.spread}%`}</strong></div>
                              <div className="mt-detail-item"><span className="fin-label"><FaDollarSign style={{marginRight: '4px'}} /> Precio</span><strong className="fin-num">${Number(t.price).toLocaleString()}</strong></div>
                              <div className="mt-detail-item"><span className="fin-label"><FaCalendarAlt style={{marginRight: '4px'}} /> Fecha</span><strong className="fin-num">{new Date(t.createdAt).toLocaleString()}</strong></div>
                              <div className="mt-detail-actions">
                                {/* ✅ FIX: navigate conectado */}
                                <button
                                  className="mt-detail-btn primary"
                                  onClick={e => { e.stopPropagation(); navigate(`/trades/${t.id}`); }}
                                >
                                  {isActive ? <><FaBolt style={{marginRight: '4px'}} /> Ir al trade</> : <><FaEye style={{marginRight: '4px'}} /> Ver detalle</>}
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-pagination">
              <button className="mt-page-btn" onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page === 1}>← Anterior</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                <button key={n} className={`mt-page-num fin-num ${page === n ? 'active' : ''}`} onClick={() => setPage(n)}>{n}</button>
              ))}
              <button className="mt-page-btn" onClick={() => setPage(p => Math.min(p + 1, totalPages))} disabled={page === totalPages}>Siguiente →</button>
              <span className="mt-page-info">Página <span className="fin-num">{page}</span> de <span className="fin-num">{totalPages}</span> · <span className="fin-num">{filtered.length}</span> trades</span>
            </div>
          )}
        </>
      )}
    </section>
  );
}

// ===========================
// PÁGINA PRINCIPAL
// ===========================
export default function MyTrades() {
  const navigate          = useNavigate();
  const { user }          = useAuth();
  const [trades, setTrades]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [refreshing, setRefresh] = useState(false);

  const fetchTrades = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefresh(true);
    try {
      const res = await apiFetch('/api/trades');
      if (!res.ok) throw new Error('Error al cargar trades');
      setTrades(await res.json());
      setError(null);
    } catch (err) { if (!isRefresh) setError(err.message); }
    finally { setLoading(false); setRefresh(false); }
  }, []);

  useEffect(() => { fetchTrades(); }, [fetchTrades]);

  // Auto-refresco suave mientras haya trades activos (refleja cambios de estado)
  const hasActive = trades.some(t => ['WAITING_PAYMENT', 'PAID', 'DISPUTED'].includes(t.status));
  useEffect(() => {
    if (!hasActive) return;
    const id = setInterval(() => fetchTrades(true), 25000);
    return () => clearInterval(id);
  }, [hasActive, fetchTrades]);

  const isEmpty = !loading && !error && trades.length === 0;

  return (
    <div className="mt-wrapper">
      <div className="mt-page">
        <div className="mt-page-header">
          <h1 className="mt-page-title"><FaGamepad /> Mis Trades</h1>
          <p className="mt-page-sub">Tu historial, métricas y progreso como trader en Bitplify.</p>
        </div>

        <StatsHero user={user} />

        {loading ? (
          <div className="mt-empty"><span><FaSpinner className="mt-spin" /></span><p>Cargando trades...</p></div>
        ) : error ? (
          <div className="mt-empty"><span><FaExclamationTriangle /></span><p>{error}</p></div>
        ) : isEmpty ? (
          <div className="mt-empty mt-empty-onboard">
            <span><FaGamepad /></span>
            <p>Todavía no hiciste ningún trade.</p>
            <p className="mt-empty-sub">Encontrá una oferta en el marketplace y hacé tu primera operación P2P — acá vas a ver tu historial, métricas y progreso.</p>
            <button className="mt-onboard-btn" onClick={() => navigate('/')}><FaArrowRight style={{ marginRight: 6 }} /> Ir al Marketplace</button>
          </div>
        ) : (
          <>
            <ActiveTradesPanel trades={trades} currentUserId={user?.id} onRefresh={() => fetchTrades(true)} refreshing={refreshing} />
            <KPICards     trades={trades} currentUserId={user?.id} />
            <VolumeChart  trades={trades} />
            <ActivityMap  trades={trades} />
            <TradeHistory trades={trades} currentUserId={user?.id} />
          </>
        )}
      </div>
    </div>
  );
}
